import React from 'react';
import PropTypes from 'prop-types';
import {debounce} from 'lodash';
import classnames from 'classnames';
import {Set} from 'immutable';
import {setUpStore, callTargetedAction} from 'client/framework';
import {MandarkTable} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {queryBuilderPropType} from '@albert-io/json-api-framework/request/builder/legacy';
import SettingsTableStore from './SettingsTable.store';
import settingsTableActions from './SettingsTable.actions';
import IconTooltip from 'sg/IconTooltip/IconTooltip.react';
import IconInput from 'sg/IconInput/IconInput.react';
import Checkbox from 'generic/Forms/Checkbox/Checkbox.react';
import TextInput from 'generic/Forms/TextInput/TextInput.react';
import './settings-table.scss';

export default class SettingsTable extends React.Component {
  static propTypes = {
    defaultSortDirection: PropTypes.string,
    title: PropTypes.string,
    query: queryBuilderPropType.isRequired,
    storeName: PropTypes.string.isRequired,
    searchFilters: PropTypes.func,
    optionsMenu: PropTypes.element,
    buttonContent: PropTypes.element,
    topFilters: PropTypes.element,
    bottomFilters: PropTypes.element,
    className: PropTypes.string,
    emptyTableElement: PropTypes.element,
    dataMutator: PropTypes.func,
    noColumnFilters: PropTypes.bool,
    rowPropsFunc: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.initializeStore();
  }

  componentWillUnmount() {
    callTargetedAction({
      name: settingsTableActions.CLEAR_SEARCH_TERM,
      targetStore: this.getStore().getName()
    });
  }

  getStore() {
    return setUpStore(SettingsTableStore, this.props.storeName);
  }

  initializeStore() {
    const store = this.getStore();
    if (!store.getVisibleColumns().isEmpty() || this.props.noColumnFilters) {
      return;
    }

    const defaultVisibleColumns = this.props.children.reduce((list, column) => {
      const columnName = column.props.heading || column.props.name;
      return column.props.defaultVisible ? list.add(columnName) : list;
    }, Set());

    callTargetedAction({
      name: settingsTableActions.SET_VISIBLE_COLUMNS,
      payload: defaultVisibleColumns,
      targetStore: store.getName()
    });
  }

  toggleColumn = (e) => {
    callTargetedAction({
      name: settingsTableActions.TOGGLE_COLUMN,
      payload: e.target.name,
      targetStore: this.getStore().getName()
    });
  };

  generateFilters() {
    if (this.props.noColumnFilters) {
      return null;
    }

    const columnFilters = this.props.children.map((column, key) => {
      if (column.props.alwaysVisible || column.props.defaultSortParam) {
        return null;
      }

      const columnName = column.props.heading || column.props.name;
      const isChecked = this.getStore().isColumnVisible(columnName);
      return (
        <Checkbox
          key={key}
          label={'Show ' + columnName}
          name={columnName}
          checked={isChecked}
          onChange={this.toggleColumn}
        />
      );
    });

    return (
      <IconTooltip
        className='settings-table__filter-dropdown'
        color='light'
        content={
          <div>
            {this.props.topFilters}
            {this.props.topFilters ? <hr /> : null}
            {columnFilters}
            {this.props.bottomFilters ? <hr /> : null}
            {this.props.bottomFilters}
          </div>
        }
        position='bottom'
        icon='fa-sliders'
        spanTitle='Options'
      />
    );
  }

  generateOptionsMenu() {
    if (!this.props.optionsMenu) {
      return null;
    }

    return (
      <IconTooltip
        className='settings-table__filter-dropdown'
        color='light'
        content={this.props.optionsMenu}
        position='bottom'
        icon='fa-ellipsis-h'
        spanTitle='Download'
      />
    );
  }

  render() {
    const store = this.getStore();
    const visibleColumns = !this.props.noColumnFilters
      ? this.props.children.filter((column) => {
          const columnName = column.props.heading || column.props.name;
          return store.isColumnVisible(columnName) || column.props.alwaysVisible;
        })
      : this.props.children;

    const term = store.getSearchTerm();
    const query =
      term && this.props.searchFilters
        ? this.props.searchFilters(this.props.query, term)
        : this.props.query;

    return (
      <div className='settings-table'>
        <div className='settings-table__header'>
          <h2>{this.props.title}</h2>
          <div className='settings-table__filters'>
            <SettingsTableSearch
              searchFilters={this.props.searchFilters}
              settingsTableStore={store}
            />
            {this.generateFilters()}
            {this.generateOptionsMenu()}
            {this.props.buttonContent}
          </div>
        </div>
        <MandarkTable
          query={query}
          storeName={this.props.storeName + 'MandarkTableStore'}
          children={visibleColumns}
          className={this.props.className}
          emptyTableElement={this.props.emptyTableElement}
          dataMutator={this.props.dataMutator}
          defaultSortDirection={this.props.defaultSortDirection}
          rowPropsFunc={this.props.rowPropsFunc}
        />
      </div>
    );
  }
}

class SettingsTableSearch extends React.Component {
  static propTypes = {
    searchFilters: PropTypes.func,
    settingsTableStore: PropTypes.instanceOf(SettingsTableStore)
  };

  onSearch = (e) => {
    this.setSearchString(e.target.value);
  };

  setSearchString = debounce((term) => {
    callTargetedAction({
      name: settingsTableActions.SET_SEARCH_TERM,
      payload: term,
      targetStore: this.props.settingsTableStore.getName()
    });
  }, 300);

  clearSearchTerm = () => {
    callTargetedAction({
      name: settingsTableActions.CLEAR_SEARCH_TERM,
      targetStore: this.props.settingsTableStore.getName()
    });

    this.searchInputNode.value = '';
  };

  render() {
    if (!this.props.searchFilters) {
      return null;
    }

    const iconClasses = classnames('fa', {
      'fa-times': this.props.settingsTableStore.getSearchTerm(),
      'fa-search': !this.props.settingsTableStore.getSearchTerm()
    });

    const onIconClick = this.props.settingsTableStore.getSearchTerm() ? this.clearSearchTerm : null;

    return (
      <IconInput
        className='settings-table__search-input'
        iconClass={iconClasses}
        onIconClick={onIconClick}
      >
        <TextInput
          placeholder='Search'
          onChange={this.onSearch}
          updateInputOnValueMismatch={false}
          value={this.props.settingsTableStore.getSearchTerm()}
          inputNodeRef={(node) => {
            this.searchInputNode = node;
          }}
        />
      </IconInput>
    );
  }
}
